/**
 * Celebration stage for the review prompt: purple glow dome, floating coins,
 * twinkles, and the monkey mascot (R2 "Win" pose) springing in then bobbing.
 * All loops are transform/opacity only (native driver).
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Image } from 'react-native';
import Svg, { Defs, Ellipse, RadialGradient, Stop } from 'react-native-svg';
import { getAssetUrl, ASSET_PATHS } from '../../../../constants/assets';
import { getReviewPromptModalStyles } from './ReviewPromptModal.styles';
import {
  REVIEW_PROMPT_COINS,
  REVIEW_PROMPT_TWINKLES,
  REVIEW_PROMPT_COLORS,
  REVIEW_PROMPT_TIMING,
} from './reviewPromptModal.constants';

const MONKEY_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.happy);
const COIN_URI = getAssetUrl(ASSET_PATHS.COINS.COIN_IDLE);

const GLOW_WIDTH = 260;
const GLOW_HEIGHT = 200;

function useLoop(durationMs: number, delayMs: number): Animated.Value {
  const value = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.delay(delayMs),
        Animated.timing(value, {
          toValue: 1,
          duration: durationMs / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(value, {
          toValue: 0,
          duration: durationMs / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [value, durationMs, delayMs]);
  return value;
}

const FloatingCoin: React.FC<{ index: number }> = ({ index }) => {
  const styles = useMemo(() => getReviewPromptModalStyles(), []);
  const spec = REVIEW_PROMPT_COINS[index];
  const float = useLoop(spec.durationMs, spec.delayMs);
  const translateY = float.interpolate({ inputRange: [0, 1], outputRange: [0, -12] });
  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.coin,
        {
          left: spec.left,
          top: spec.top,
          width: spec.size,
          height: spec.size,
          transform: [{ translateY }, { rotate: `${spec.rotateDeg}deg` }],
        },
      ]}
    >
      <Image source={{ uri: COIN_URI }} style={styles.coinImage} resizeMode="contain" />
    </Animated.View>
  );
};

const Twinkle: React.FC<{ index: number }> = ({ index }) => {
  const styles = useMemo(() => getReviewPromptModalStyles(), []);
  const spec = REVIEW_PROMPT_TWINKLES[index];
  const pulse = useLoop(spec.durationMs, spec.delayMs);
  const opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.15, 0.9] });
  const scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.7, 1.1] });
  const { fontSize, color, left, right, top, bottom } = spec;
  return (
    <Animated.Text
      style={[
        styles.twinkle,
        { fontSize, color, left, right, top, bottom, opacity, transform: [{ scale }] },
      ]}
    >
      ✦
    </Animated.Text>
  );
};

export const ReviewPromptMascotStage: React.FC = () => {
  const styles = useMemo(() => getReviewPromptModalStyles(), []);
  const enter = useRef(new Animated.Value(0)).current;
  const bob = useLoop(REVIEW_PROMPT_TIMING.MONKEY_BOB_MS, 900);
  const glow = useLoop(3200, 0);

  useEffect(() => {
    const spring = Animated.spring(enter, {
      toValue: 1,
      delay: REVIEW_PROMPT_TIMING.MONKEY_IN_DELAY_MS,
      friction: 6,
      tension: 60,
      useNativeDriver: true,
    });
    spring.start();
    return () => spring.stop();
  }, [enter]);

  const enterY = enter.interpolate({ inputRange: [0, 1], outputRange: [30, 0] });
  const bobY = bob.interpolate({ inputRange: [0, 1], outputRange: [0, -8] });
  const scale = enter.interpolate({ inputRange: [0, 1], outputRange: [0.6, 1] });
  const glowOpacity = glow.interpolate({ inputRange: [0, 1], outputRange: [0.55, 0.85] });

  return (
    <Animated.View style={styles.stage}>
      <Animated.View pointerEvents="none" style={[styles.stageGlow, { opacity: glowOpacity }]}>
        <Svg width={GLOW_WIDTH} height={GLOW_HEIGHT}>
          <Defs>
            <RadialGradient id="reviewPromptDome" cx="50%" cy="100%" rx="55%" ry="95%">
              <Stop offset="0" stopColor={REVIEW_PROMPT_COLORS.GLOW_INNER} stopOpacity="1" />
              <Stop offset="0.55" stopColor={REVIEW_PROMPT_COLORS.GLOW_MID} stopOpacity="0.9" />
              <Stop offset="1" stopColor={REVIEW_PROMPT_COLORS.GLOW_MID} stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse
            cx={GLOW_WIDTH / 2}
            cy={GLOW_HEIGHT}
            rx={GLOW_WIDTH / 2}
            ry={GLOW_HEIGHT}
            fill="url(#reviewPromptDome)"
          />
        </Svg>
      </Animated.View>

      {REVIEW_PROMPT_COINS.map((_, index) => (
        <FloatingCoin key={`coin-${index}`} index={index} />
      ))}
      {REVIEW_PROMPT_TWINKLES.map((_, index) => (
        <Twinkle key={`twinkle-${index}`} index={index} />
      ))}

      <Animated.View
        style={{ opacity: enter, transform: [{ translateY: Animated.add(enterY, bobY) }, { scale }] }}
      >
        <Image
          source={{ uri: MONKEY_URI }}
          style={styles.monkey}
          resizeMode="contain"
          accessibilityLabel="Zporti mascot celebrating"
        />
      </Animated.View>
    </Animated.View>
  );
};
